// A bounded fan-out, shared by the browser's fleet-wide pages and the
// wx-poller Lambda.
//
// The Trips page and the place-visits page both answer a question by asking
// EVERY item in the fleet (README § "The Trips page", § "Place visits"), and a
// bare Promise.all over the items is a burst of two or three AppSync requests
// per item, all at once, unbounded as the fleet grows. The poller has the same
// shape against government hosts. This is the one pool both use.
//
// Ordered: results[i] is task(items[i]) whatever order the tasks finished in.
// A rejection rejects the whole call, exactly as Promise.all would; a caller
// that wants per-item isolation catches inside `task` (usePlaceRecords does).
// `signal` stops the pool SCHEDULING new work once it aborts; a task already
// running is the task's own business (pass the signal on to its fetches).

// Items in flight at once for a per-item fleet read. Each item issues two or
// three queries, so this is eight to twelve concurrent requests -- enough to
// keep a small fleet as fast as it was, and a ceiling for a large one.
export const FLEET_READ_CONCURRENCY = 4;

export async function mapWithConcurrency(items, limit, task, { signal = null } = {}) {
  const results = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    for (;;) {
      if (signal?.aborted) return;
      const index = next;
      next += 1;
      if (index >= items.length) return;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await task(items[index], index);
    }
  });
  await Promise.all(workers);
  return results;
}
